import { LitElement, html } from "lit";
import FormUploadElement from "./form-upload";
import ProgressDivElement from "./progress-div";
import FormUploadCompleteElement from "./form-upload-complete";

// Duplicated from static/script/index.js (not imported — that script isn't a module).
// Visitors may reach the app via http(s):// and/or `www.<domain>` — normalize the
// displayed link to a bare host (scheme-less, no `www.`) so it's consistent and
// matches the scheme-less format used elsewhere (see static/index.html's placeholder value).
const getDisplayOrigin = (origin) => origin.replace(/^https?:\/\/(www\.)?/i, '');

// Ported from static/script/index.js's uploadChunk, plus an AbortSignal wired to
// xhr.abort() so #startUpload can cancel an in-flight chunk when a newer upload
// supersedes it.
const uploadChunk = (uploadId, chunk, uploadedBytes, totalBytes, signal, onProgress) => new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('chunk', chunk);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
            onProgress(Math.round(((uploadedBytes + e.loaded) / totalBytes) * 100));
        }
    });

    xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
            try {
                resolve(JSON.parse(xhr.responseText));
            } catch (_error) {
                reject(new Error('Invalid chunk response.'));
            }
        } else {
            reject(new Error('Chunk upload failed.'));
        }
    });

    xhr.addEventListener('error', () => reject(new Error('Chunk upload failed.')));
    xhr.addEventListener('abort', () => reject(new DOMException('Upload aborted.', 'AbortError')));

    signal.addEventListener('abort', () => xhr.abort());

    xhr.open('POST', `/upload/chunk/${uploadId}`);
    xhr.send(formData);
});

class UploadContainer extends LitElement {
    #uploadAbortController = null;

    // On upload-button-clicked (dispatched by tempup-form-upload, see
    // _plans/4.2-lit_form_upload_component.md Step 13), this ancestor owns the
    // cross-component visibility swap neither sibling can decide on its own.
    #handleUploadButtonClicked(e) {
        const selectedFile = e.target.selectedFile;
        if (!selectedFile) {
            return;
        }

        const progressDiv = this.renderRoot.querySelector('tempup-progress-div');

        e.target.hidden = true;
        progressDiv.hidden = false;
        progressDiv.fileName = selectedFile.name;
        progressDiv.focusProgressBar();

        // Announces the phase change; static/script/index.js listens on document and
        // reuses its own setHeaderVisibility to react — see _plans/4.5-lit_upload_container_component.md Step 7.
        this.dispatchEvent(new CustomEvent('upload-started', { bubbles: true, composed: true }));

        this.#startUpload(selectedFile);
    }

    // On upload-another-clicked (dispatched by tempup-form-upload-complete), reset
    // and re-show the form.
    #handleUploadAnotherClicked(e) {
        const formUpload = this.renderRoot.querySelector('tempup-form-upload');
        formUpload.reset();

        e.target.hidden = true;
        formUpload.hidden = false;
        formUpload.focusFileSelect();

        // See upload-started above — same events-up pattern, opposite direction.
        this.dispatchEvent(new CustomEvent('upload-reset', { bubbles: true, composed: true }));
    }

    // Cancels any upload already in flight before starting a new one, so "restart"
    // is an explicit decision (a fresh AbortController) rather than something inferred
    // from #handleUploadButtonClicked firing twice.
    async #startUpload(file) {
        this.#uploadAbortController?.abort();
        const abortController = new AbortController();
        this.#uploadAbortController = abortController;

        const progressDiv = this.renderRoot.querySelector('tempup-progress-div');
        progressDiv.progressValue = 0;

        try {
            const { uploadId, allowedChunkSize } = await fetch('/upload/init', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: file.name, fileSize: file.size }),
                signal: abortController.signal,
            }).then((r) => r.json());

            const totalChunks = Math.ceil(file.size / allowedChunkSize);
            let uploadedBytes = 0;

            for (let i = 0; i < totalChunks; i++) {
                const chunk = file.slice(i * allowedChunkSize, (i + 1) * allowedChunkSize);
                await uploadChunk(uploadId, chunk, uploadedBytes, file.size, abortController.signal, (percent) => {
                    progressDiv.progressValue = percent;
                });
                uploadedBytes += chunk.size;
            }

            progressDiv.progressValue = 100;

            const formUploadComplete = this.renderRoot.querySelector('tempup-form-upload-complete');
            formUploadComplete.downloadLink = `${getDisplayOrigin(window.location.origin)}/file/${uploadId}`;
            progressDiv.hidden = true;
            formUploadComplete.hidden = false;
            formUploadComplete.focusDownloadLink();
        } catch (error) {
            // A superseded upload rejects here too (uploadChunk's abort listener, or
            // fetch's own AbortSignal handling) — not a real failure, nothing to report.
            if (abortController.signal.aborted) return;

            // TODO: no failure UI yet — static/script/index.js's finishUpload('') path
            // (reset to the form, show the header again) isn't ported to the Lit
            // components yet. See plan's Known outstanding TODOs.
        }
    }

    render() {
        return html`
            <tempup-form-upload @upload-button-clicked=${this.#handleUploadButtonClicked}></tempup-form-upload>
            <tempup-progress-div hidden></tempup-progress-div>
            <tempup-form-upload-complete hidden @upload-another-clicked=${this.#handleUploadAnotherClicked}></tempup-form-upload-complete>
        `;
    }
}

customElements.define("upload-container", UploadContainer)